import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  MessageSquare,
  Newspaper,
  UserCircle2,
  ChevronRight,
  ShieldCheck,
  Sparkles,
  Trophy,
  Clock,
  Heart,
  Lightbulb,
  Mic,
  ArrowRight,
} from 'lucide-react';
import { useT } from '../context/LanguageContext';

const OBJECTIVES = [
  'Comprendre des phrases simples',
  'Lire de courtes histoires',
  'Reconnaître le vocabulaire du quotidien',
  'Identifier les informations importantes',
  'Répondre à des questions simples',
  "Parler d'un texte avec vos propres mots",
];

const PROGRESS = [
  { label: 'Histoires', done: 0, total: 10 },
  { label: 'Dialogues', done: 0, total: 10 },
  { label: 'Articles', done: 0, total: 10 },
  { label: 'Témoignages', done: 0, total: 5 },
];

const STORIES = [
  { id: 'ma-nouvelle-amie', title: 'L\'amitié', subtitle: 'Ma nouvelle amie', time: '2 min', available: true,
    img: 'https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=600&q=70' },
  { id: 'perseverance', title: 'La persévérance', time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1551817958-d9d86fb29431?auto=format&fit=crop&w=600&q=70' },
  { id: 'confiance', title: 'La confiance en soi', time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1531171596281-8b5d26917d8b?auto=format&fit=crop&w=600&q=70' },
  { id: 'difference', title: 'La différence est une richesse', time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=600&q=70' },
  { id: 'gratitude', title: 'La gratitude', time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=600&q=70' },
  { id: 'nature', title: 'Respecter la nature', time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=600&q=70' },
  { id: 'courage', title: 'Le courage', time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=600&q=70' },
  { id: 'partage', title: 'Le partage', time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=600&q=70' },
  { id: 'honnetete', title: "L'honnêteté", time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?auto=format&fit=crop&w=600&q=70' },
  { id: 'connaissance', title: "La connaissance vaut plus que l'argent", time: '2 min', available: false,
    img: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=600&q=70' },
];

const JOURNEY = [
  { icon: BookOpen, label: 'Lire', desc: 'Lisez le texte.' },
  { icon: Lightbulb, label: 'Comprendre', desc: 'Répondez aux questions.' },
  { icon: Sparkles, label: 'Apprendre', desc: 'Découvrez le vocabulaire important.' },
  { icon: Heart, label: 'Réagir', desc: 'Donnez votre opinion.' },
  { icon: Mic, label: 'Parler', desc: 'Enregistrez votre réponse orale.' },
];

export default function LireComprendreA1() {
  const t = useT();
  const totalDone = PROGRESS.reduce((s, p) => s + p.done, 0);
  const totalAll = PROGRESS.reduce((s, p) => s + p.total, 0);

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="lire-comprendre-a1-page">
      {/* HERO */}
      <section className="pt-16 pb-12">
        <div className="max-w-[920px] mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="text-[12px] text-[#6B4E37] mb-8 flex items-center gap-1.5 flex-wrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Selora</Link>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <Link to="/lire-le-francais" className="hover:text-[#D4AF37] transition-colors">Lire le français</Link>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <span className="hover:text-[#D4AF37] transition-colors">Lire &amp; Comprendre</span>
            <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-[#1A2F4A] font-semibold">A1</span>
          </nav>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-5">
              <div className="w-12 h-12 rounded-full bg-[#1A2F4A] text-[#D4AF37] flex items-center justify-center font-bold text-sm tracking-wider">
                A1
              </div>
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold">Lire &amp; Comprendre · Découverte</p>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2F4A] mb-5 leading-[1.05]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title">
              A1 – Lire &amp; Comprendre
            </h1>

            <p
              className="text-base sm:text-lg text-[#6B4E37] italic max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-subtitle">
              {t({
                fr: 'Découvrez le plaisir de lire en français grâce à des histoires, des dialogues et des textes simples conçus pour les débutants.',
                en: 'Discover the pleasure of reading in French through stories, dialogues and simple texts designed for beginners.',
              })}
            </p>
          </div>

          <div className="mt-10 max-w-2xl mx-auto text-[#1A2F4A]/85 text-[15px] leading-relaxed space-y-4" data-testid="hero-intro">
            <p>
              {t({
                fr: 'À ce niveau, vous allez apprendre à comprendre des phrases simples, enrichir votre vocabulaire et développer votre confiance en lecture.',
                en: 'At this level, you will learn to understand simple sentences, enrich your vocabulary and build your reading confidence.',
              })}
            </p>
            <p>
              {t({
                fr: <>Chaque activité a été créée pour vous aider à <em>lire</em>, <em>comprendre</em> et <em>parler français</em> naturellement.</>,
                en: <>Every activity has been designed to help you <em>read</em>, <em>understand</em> and <em>speak French</em> naturally.</>,
              })}
            </p>
          </div>
        </div>
      </section>

      {/* OBJECTIVES */}
      <section className="pb-14" data-testid="objectives-section">
        <div className="max-w-[1000px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Objectifs</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="objectives-title">
              Ce que vous allez apprendre
            </h2>
            <p className="text-sm text-[#6B4E37] italic mt-3">À la fin de ce niveau, vous serez capable de…</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="objectives-grid">
            {OBJECTIVES.map((obj, i) => (
              <div
                key={i}
                className="bg-white border border-[#E8E4DC] hover:border-[#D4AF37] rounded-2xl px-5 py-5 transition-all hover:shadow-md flex items-start gap-3"
                data-testid={`objective-${i}`}>
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] text-sm font-bold">✓</span>
                </div>
                <p className="text-[14px] text-[#1A2F4A] leading-relaxed">{obj}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRESS */}
      <section className="pb-14" data-testid="progress-section">
        <div className="max-w-[1000px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Suivi</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="progress-title">
              Votre progression
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="progress-grid">
            {PROGRESS.map((p, i) => {
              const pct = Math.round((p.done / p.total) * 100);
              return (
                <div
                  key={p.label}
                  className="bg-white border border-[#E8E4DC] rounded-2xl px-5 py-5"
                  data-testid={`progress-${p.label.toLowerCase()}`}>
                  <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-2">{p.label}</p>
                  <p className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {p.done} <span className="text-[#999] text-base font-normal">/ {p.total}</span>
                  </p>
                  <div className="h-1.5 bg-[#F3F0E8] rounded-full mt-3 overflow-hidden">
                    <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] text-white rounded-2xl px-6 py-5 flex items-center justify-between" data-testid="progress-total">
            <div>
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">Total</p>
              <p className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                {totalDone} <span className="text-white/60 text-sm font-normal">/ {totalAll} activités</span>
              </p>
            </div>
            <Trophy className="w-7 h-7 text-[#D4AF37]" />
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="pb-14" data-testid="categories-section">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Contenus</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              Choisissez votre format de lecture
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* CARD 1 — Histoires */}
            <div className="bg-white border border-[#D4AF37]/30 rounded-2xl px-6 py-6 flex flex-col" data-testid="card-histoires">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/40 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[9px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">Histoires</p>
                  <h3 className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    📖 Histoires
                  </h3>
                </div>
              </div>
              <p className="text-[13px] text-[#6B4E37] leading-relaxed mb-4">
                Découvrez des histoires originales conçues spécialement pour les apprenants A1.
              </p>
              <p className="text-[12px] text-[#1A2F4A]/85 italic mb-5">10 histoires disponibles.</p>

              <div className="grid sm:grid-cols-2 gap-3" data-testid="stories-grid">
                {STORIES.map((s, i) => {
                  const inner = (
                    <div className={`group bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl overflow-hidden ${s.available ? 'hover:border-[#D4AF37] hover:shadow-md cursor-pointer' : 'opacity-70'} transition-all`}>
                      <div className="relative h-24 bg-gradient-to-br from-[#1A2F4A]/10 to-[#D4AF37]/10 overflow-hidden">
                        <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute top-2 left-2 bg-[#1A2F4A] text-[#D4AF37] text-[9px] font-bold px-2 py-0.5 rounded-full tracking-wider">A1</div>
                        <div className="absolute top-2 right-2 bg-white/90 text-[#1A2F4A] text-[9px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" /> {s.time}
                        </div>
                      </div>
                      <div className="px-3 py-3">
                        <p className="text-[8px] text-[#D4AF37] uppercase tracking-[0.2em] font-bold mb-1 flex items-center gap-1">
                          <span aria-hidden="true">✦</span> Création Originale
                        </p>
                        <p className="text-[13px] font-bold text-[#1A2F4A] leading-tight mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {i + 1}. {s.title}
                        </p>
                        {s.subtitle && <p className="text-[11px] text-[#6B4E37] italic mb-2">{s.subtitle}</p>}
                        <div className={`text-[11px] font-semibold flex items-center gap-1 mt-2 ${s.available ? 'text-[#1A2F4A] group-hover:text-[#D4AF37]' : 'text-[#999]'}`}>
                          {s.available ? <>Lire l'histoire <ArrowRight className="w-3 h-3" /></> : 'Bientôt disponible'}
                        </div>
                      </div>
                    </div>
                  );
                  return s.available ? (
                    <Link key={s.id} to={`/lire-le-francais/lire-et-comprendre/a1/histoires/${s.id}`} data-testid={`story-${s.id}`}>{inner}</Link>
                  ) : (
                    <div key={s.id} data-testid={`story-${s.id}`}>{inner}</div>
                  );
                })}
              </div>
            </div>

            {/* CARDS 2, 3, 4 stacked */}
            <div className="flex flex-col gap-5">
              <ComingSoonCard icon={MessageSquare} eyebrow="Dialogues" title="💬 Dialogues" desc="10 dialogues de la vie quotidienne." testid="card-dialogues" />
              <ComingSoonCard icon={Newspaper} eyebrow="Articles" title="📰 Articles" desc="10 articles simples adaptés aux débutants." testid="card-articles" />
              <ComingSoonCard icon={UserCircle2} eyebrow="Témoignages" title="👤 Témoignages" desc="5 récits personnels authentiques." testid="card-temoignages" />
            </div>
          </div>
        </div>
      </section>

      {/* HOW EACH ACTIVITY WORKS */}
      <section className="pb-14 bg-white border-y border-[#E8E4DC]" data-testid="journey-section">
        <div className="max-w-[1100px] mx-auto px-6 pt-12">
          <div className="text-center mb-10">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Méthode</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              Comment fonctionne chaque activité ?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 relative" data-testid="journey-grid">
            {JOURNEY.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="bg-[#FAF7F0] border border-[#E8E4DC] hover:border-[#D4AF37] rounded-2xl px-4 py-5 text-center transition-all relative" data-testid={`journey-step-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-[#1A2F4A] text-[#D4AF37] flex items-center justify-center mx-auto mb-3 font-bold text-sm">
                    {i + 1}
                  </div>
                  <Icon className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-[14px] font-bold text-[#1A2F4A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{step.label}</p>
                  <p className="text-[11px] text-[#6B4E37] leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CRÉATION ORIGINALE BADGE */}
      <section className="py-14" data-testid="creation-badge-section">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl px-7 py-7 text-white overflow-hidden relative">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D4AF37]/10 rounded-full" aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold">Label éditorial</p>
                  <h3 className="text-xl font-bold leading-tight flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <span className="text-[#D4AF37]" aria-hidden="true">✦</span>
                    Création Originale Selora
                  </h3>
                </div>
              </div>
              <p className="text-[14px] text-white/90 leading-relaxed">
                Tous les textes de ce niveau sont spécialement conçus pour les apprenants débutants afin de développer
                progressivement la compréhension, le vocabulaire et la confiance en lecture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MOTIVATION */}
      <section className="pb-14" data-testid="motivation-section">
        <div className="max-w-[760px] mx-auto px-6 text-center">
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Encouragement</p>
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            N'ayez pas peur de lire
          </h2>
          <div className="space-y-3 text-[15px] text-[#1A2F4A]/85 leading-relaxed">
            <p>Vous n'avez pas besoin de comprendre chaque mot.</p>
            <p>L'objectif est de comprendre le sens général, d'apprendre progressivement et de prendre plaisir à lire en français.</p>
            <p className="italic text-[#6B4E37]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Chaque lecture est une nouvelle étape vers la maîtrise de la langue.
            </p>
          </div>
        </div>
      </section>

      {/* READING CHALLENGE */}
      <section className="pb-16" data-testid="challenge-section">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="bg-white border border-[#D4AF37]/40 rounded-2xl px-7 py-7 relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#D4AF37]/10 rounded-full" aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-[#1A2F4A] flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold">Défi</p>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1A2F4A] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Défi Lecture A1
                  </h2>
                </div>
              </div>

              <ul className="space-y-2 text-[14px] text-[#1A2F4A]/90 mb-5">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />Lisez les 10 histoires de ce niveau.</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />Complétez toutes les activités.</li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />Développez votre confiance en lecture.</li>
              </ul>

              <div className="inline-flex items-center gap-2 bg-[#FAF7F0] border border-[#D4AF37]/40 rounded-full px-4 py-2" data-testid="badge-lecteur-a1">
                <span className="text-base" aria-hidden="true">🏆</span>
                <span className="text-[12px] font-bold text-[#1A2F4A] tracking-wide">Lecteur A1</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY CLOSING */}
      <section className="pb-20" data-testid="philosophy-closing">
        <div className="max-w-[760px] mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-8 bg-[#D4AF37]/50" />
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <div className="h-px w-8 bg-[#D4AF37]/50" />
          </div>
          <p className="text-xl sm:text-2xl text-[#1A2F4A] leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            Lire pour comprendre.<br />
            Comprendre pour réfléchir.<br />
            Réfléchir pour s'exprimer.
          </p>
        </div>
      </section>
    </div>
  );
}

function ComingSoonCard({ icon: Icon, eyebrow, title, desc, testid }) {
  return (
    <div className="bg-white border border-[#E8E4DC] rounded-2xl px-6 py-6 flex-1 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/30 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <div>
          <p className="text-[9px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">{eyebrow}</p>
          <h3 className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
        </div>
      </div>
      <p className="text-[13px] text-[#6B4E37] leading-relaxed mb-4 flex-1">{desc}</p>
      <p className="text-[11px] text-[#999] italic flex items-center gap-1">
        <Sparkles className="w-3 h-3 text-[#D4AF37]" />
        Bientôt disponible
      </p>
    </div>
  );
}
